import { useState } from 'react';
import Hero from '../components/Hero';

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: 'What is the ICCAC Evidence-Based Accreditation Framework?',
      answer: 'The EBAF is ICCAC\'s founding intellectual innovation: a system that classifies every coaching modality by its actual evidence level, from Tier 1 with gold-standard clinical validation through to Tier 3 grounded in contemplative tradition. It sets honest, rigorous standards for each tier and requires coaches to represent the evidence for their work accurately to clients. No other coaching body has built a framework like this.'
    },
    {
      question: 'Is ICCAC recognized internationally?',
      answer: 'Yes. ICCAC credentials are recognized globally and designed to be compatible with international coaching standards, complementing the competency frameworks of ICF, EMCC, and other major bodies. ICCAC is not a competitor to these organizations, it serves the specific segment of consciousness-based coaching that they were not designed to fully address.'
    },
    {
      question: 'How long does accreditation take?',
      answer: 'Individual credential accreditation typically takes 15 to 30 working days from receipt of a complete application. Institutional accreditation includes a full Evidence Audit review and typically takes 30 to 45 working days. Applications with complete documentation consistently process faster.'
    },
    {
      question: 'My training covered a Tier 3 modality. Can I still be accredited?',
      answer: 'Absolutely. Tier 3 is not a lesser category, it is an honest one. ICCAC accredits coaches in contemplative and philosophically-grounded modalities with great respect for the depth of those traditions. What we require is that coaches in Tier 3 domains represent their work accurately to clients as grounded in contemplative practice rather than clinical evidence. This builds more durable trust than overclaiming the science ever could.'
    },
    {
      question: 'Can I get accredited if my training was with another organization?',
      answer: 'Yes. ICCAC accredits coaches trained by any recognized coaching institute, as long as your training, experience, and competence meet our standards. Recognition of Prior Learning is available for up to 50 percent of required training hours.'
    },
    {
      question: 'Does ICCAC offer scholarships or payment plans?',
      answer: 'Yes. We offer installment payment options for all certification programs and have a scholarship program for coaches from developing economies and coaches demonstrating financial hardship. Contact scholarships@iccacouncil.com for information and application forms.'
    },
    {
      question: 'What makes ICCAC different from other coaching accreditation bodies?',
      answer: 'ICCAC is the only accreditation body that classifies every coaching modality by its actual evidence level. We don\'t treat all coaching methods the same - we honestly assess what science says and what it doesn\'t say. This means coaches know exactly what they can claim, and clients know exactly what they\'re consenting to.'
    },
    {
      question: 'How much does accreditation cost?',
      answer: 'Accreditation costs vary by credential level. Individual accreditation starts at the Associate level, with Professional and Master levels requiring more experience and training hours. Visit our Accreditation page for detailed pricing, or contact accreditation@iccacouncil.com for specific information about your situation.'
    },
    {
      question: 'Do I need to maintain my accreditation?',
      answer: 'Yes. All ICCAC credentials require ongoing professional development, including annual Evidence Literacy updates, supervision hours, and adherence to our Code of Ethics. This ensures that our credentialed coaches stay current with the latest research and maintain the highest professional standards.'
    },
    {
      question: 'Can I display my ICCAC credential on my website and marketing materials?',
      answer: 'Absolutely! Once accredited, you receive a digital credential badge that is QR-linked and independently verifiable. You can display this on your website, LinkedIn, business cards, and all professional materials with confidence.'
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div>
      <Hero
        title="Frequently Asked Questions"
        subtitle="Everything you need to know about ICCAC accreditation and certification"
      />

      <div className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-lg shadow-md overflow-hidden"
                data-testid={`faq-item-${index}`}
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition-colors"
                  data-testid={`faq-question-${index}`}
                >
                  <span className="text-lg font-semibold text-[#1e3a5f] pr-4">
                    {faq.question}
                  </span>
                  <svg
                    className={`w-6 h-6 text-[#1e3a5f] flex-shrink-0 transition-transform ${
                      openIndex === index ? 'transform rotate-180' : ''
                    }`}
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path d="M19 9l-7 7-7-7"></path>
                  </svg>
                </button>
                {openIndex === index && (
                  <div
                    className="px-6 py-4 bg-gray-50 border-t border-gray-200"
                    data-testid={`faq-answer-${index}`}
                  >
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Still Have Questions */}
      <div className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-[#1e3a5f] mb-4">
            Still Have Questions?
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            We're here to help! Contact our team for personalized assistance.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="mailto:info@iccacouncil.com"
              className="bg-[#1e3a5f] text-white px-8 py-3 rounded-md font-semibold hover:bg-[#2d5380] transition-colors"
            >
              Email Us
            </a>
            <a
              href="/contact"
              className="bg-gray-100 text-[#1e3a5f] px-8 py-3 rounded-md font-semibold hover:bg-gray-200 transition-colors"
            >
              Contact Form
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQ;
